import { Badge, Body1, Button, Caption1, Divider, Subtitle2, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircleFilled, LockClosedRegular } from '@fluentui/react-icons';

import { SECTION_STATUS, SYSTEM_CHANGE_FIELD_KEY, TAssessmentSectionConfig, TChangeV2 } from '../../models';
import {
  describeCondition,
  evaluateSectionCompletion,
  formatDateTime,
  isCustomFieldRequired,
  isCustomFieldVisible,
  isQuestionRequired,
  isQuestionVisible,
  makeValueResolver,
} from '../../utils';
import { useChangeV2Store } from '../../state/ChangeV2Store';
import { AssessmentItemRow } from './AssessmentItemRow';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalXL,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
  },
  head: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  headRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  meta: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3 },
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  missing: { color: tokens.colorNeutralForeground3 },
});

const PLATFORM_KEY_LABELS: Record<string, string> = {
  [SYSTEM_CHANGE_FIELD_KEY.KNOWN_SAFETY_CONCERN]: 'known safety concern',
  [SYSTEM_CHANGE_FIELD_KEY.CHANGE_CATEGORY]: 'change category',
  [SYSTEM_CHANGE_FIELD_KEY.SCOPE]: 'scope',
  [SYSTEM_CHANGE_FIELD_KEY.CRITICALITY]: 'criticality',
};

type TProps = {
  change: TChangeV2;
  section: TAssessmentSectionConfig;
  readOnly: boolean;
  onCompleted?: () => void;
};

// Renders one assessment section: purpose, responsible function, status, its
// configured custom fields and questions (conditionally visible), and the
// mark-complete action. All fields render from configuration.
export const AssessmentSection = ({ change, section, readOnly, onCompleted }: TProps) => {
  const classes = useStyles();
  const {
    setAssessmentAnswer,
    setCustomFieldValue,
    setAnswerComment,
    confirmAnswerSuggestion,
    completeSection,
    reopenSection,
  } = useChangeV2Store();

  const assessment = change.assessment;
  if (!assessment) return null;

  const resolve = makeValueResolver(assessment, change);
  const progress = assessment.sectionProgress[section.id];
  const isComplete = progress?.status === SECTION_STATUS.COMPLETE;
  const completion = evaluateSectionCompletion(section, assessment, change, assessment.snapshot.customFields);

  const labelForKey = (key: string): string => {
    const question = assessment.snapshot.assessmentSections.flatMap((s) => s.questions).find((q) => q.id === key);
    if (question) return question.label.toLocaleLowerCase();
    const field = assessment.snapshot.customFields.find((f) => f.id === key);
    if (field) return field.label.toLocaleLowerCase();
    return PLATFORM_KEY_LABELS[key] ?? key;
  };

  const visibleCustomFields = section.customFieldIds
    .map((fieldId) => assessment.snapshot.customFields.find((field) => field.id === fieldId))
    .filter((field): field is NonNullable<typeof field> => Boolean(field) && isCustomFieldVisible(field!, resolve));

  const visibleQuestions = section.questions.filter((question) => isQuestionVisible(question, resolve));

  return (
    <div className={classes.root}>
      <div className={classes.head}>
        <div className={classes.headRow}>
          <Subtitle2>{section.title}</Subtitle2>
          {isComplete ? (
            <Badge appearance="tint" color="success" icon={<CheckmarkCircleFilled />}>
              Complete
            </Badge>
          ) : (
            <Badge
              appearance="tint"
              color={progress?.status === SECTION_STATUS.IN_PROGRESS ? 'warning' : 'informative'}
            >
              {progress?.status ?? SECTION_STATUS.NOT_STARTED}
            </Badge>
          )}
        </div>
        <Caption1 className={classes.muted}>{section.description}</Caption1>
        <div className={classes.meta}>
          <Caption1 className={classes.muted}>Responsible: {section.responsibleFunction}</Caption1>
          <Caption1 className={classes.muted}>
            · {completion.requiredAnswered}/{completion.requiredTotal} required answered
          </Caption1>
        </div>
      </div>

      <Divider />

      <div className={classes.group}>
        {visibleCustomFields.map((field) => {
          const fieldValue = assessment.customFieldValues[field.id];
          if (!fieldValue) return null;
          return (
            <AssessmentItemRow
              key={field.id}
              id={field.id}
              label={field.label}
              required={isCustomFieldRequired(field, resolve)}
              helpText={field.helpText}
              controlType={field.fieldType}
              options={field.options}
              value={fieldValue}
              disabled={readOnly}
              shownBecause={field.visibleWhen ? describeCondition(field.visibleWhen, labelForKey) : undefined}
              onChange={(value) => setCustomFieldValue(change.id, field.id, value)}
            />
          );
        })}

        {visibleQuestions.map((question) => {
          const answer = assessment.answers[question.id];
          if (!answer) return null;
          return (
            <AssessmentItemRow
              key={question.id}
              id={question.id}
              label={question.label}
              required={isQuestionRequired(question, resolve)}
              helpText={question.helpText}
              controlType={question.controlType}
              options={question.options}
              value={answer}
              disabled={readOnly}
              shownBecause={question.visibleWhen ? describeCondition(question.visibleWhen, labelForKey) : undefined}
              confirmationNote={question.confirmationNote}
              allowsComment={question.allowsComment}
              onChange={(value) => setAssessmentAnswer(change.id, question.id, value)}
              onComment={(comment) => setAnswerComment(change.id, question.id, comment)}
              onConfirm={() => confirmAnswerSuggestion(change.id, question.id)}
            />
          );
        })}
      </div>

      <Divider />

      <div className={classes.footer}>
        {isComplete ? (
          <Caption1 className={classes.muted}>
            Completed by {progress?.completedBy}
            {progress?.completedAt ? ` · ${formatDateTime(progress.completedAt)}` : ''}
          </Caption1>
        ) : completion.complete ? (
          <Caption1 className={classes.muted}>You can continue with these answers or skip this section.</Caption1>
        ) : (
          <Caption1 className={classes.missing}>Remaining: {completion.missing.join(', ')}</Caption1>
        )}

        {readOnly ? (
          <Badge appearance="ghost" color="subtle" icon={<LockClosedRegular />}>
            Read-only
          </Badge>
        ) : isComplete ? (
          <Button appearance="secondary" onClick={() => reopenSection(change.id, section.id)}>
            Reopen section
          </Button>
        ) : (
          <Button
            appearance="primary"
            disabled={!completion.complete}
            onClick={() => {
              completeSection(change.id, section.id);
              onCompleted?.();
            }}
          >
            Continue
          </Button>
        )}
      </div>
    </div>
  );
};
